import { ANGEL_STUDIOS_EMAIL_GROUP_ID } from '@/constants/email'
import { paths } from '@/constants/paths'
import { CatalogMovie, CatalogSeries, formatNonTitleLikeLandscapeImagePath } from '@/services/ContentCatalog'
import { inTheaters, isInGuildAccess, isSellingTickets, isStreaming } from '@/services/PhaseManagerService'
import { ProjectMeta } from '@/services/ProjectsService'
import { EpisodeWithStills } from '@/services/ProjectsService/types'
import { GetTheatricalPreReleasePageDataResult } from '@/services/TheatricalPreReleaseService'
import { ShopifyStore } from '@/types/codegen-contentful'
import { TranslateFunction } from '@/utils/translate/translate-client'

export function injectMissingCtas(
  ctas: CallToAction[],
  catalogTitle: CatalogMovie | CatalogSeries | undefined | null,
  t: TranslateFunction,
  primaryFlowPhases: ProjectMeta['primaryFlowPhases'] | undefined,
  isGuildMember: boolean,
  projectSlug: string,
  projectName: string,
  pifEnabled: boolean,
  numberAvailable?: number,
  shopifyStore?: ShopifyStore | undefined | null,
  episodesWithStills?: EpisodeWithStills[] | undefined | null,
): CallToAction[] {
  const canStream = isStreaming(primaryFlowPhases || [])
  const inGuildAccess = isInGuildAccess(primaryFlowPhases || [])
  const hasTickets = isSellingTickets(primaryFlowPhases || [])
  const isInTheaters = inTheaters(primaryFlowPhases || [])

  const bgs = buildBackgrounds(catalogTitle, episodesWithStills)
  const baseEmailCapture = buildBaseEmailCapture(t, bgs.signUp)

  if (ctas && ctas.length > 0) {
    const items = [...ctas]
    const hasStore = items.some((c) => c?.buttonHref?.startsWith(paths.external.shop.index))
    const hasPif = items.some((c) => c?.buttonHref?.includes(paths.payItForward.index))
    const hasEmailCapture = items.some((c) => c?.type === 'Email Capture')
    if (!hasStore && shopifyStore?.storeUrl)
      items.push(
        buildShopCta(baseEmailCapture, {
          t,
          storeUrl: shopifyStore?.storeUrl,
          isGuildMember,
          backgroundImg: bgs.gifts,
        }),
      )
    if (!hasPif && pifEnabled) items.push(buildPifCta(baseEmailCapture, { t, projectSlug, backgroundImg: bgs.pif }))
    if (!hasEmailCapture) items.push(baseEmailCapture)
    return items
  }

  const items: CallToAction[] = []

  if (canStream) {
    items.push(
      buildStreamingCta(baseEmailCapture, {
        t,
        inGuildAccess,
        isGuildMember,
        projectSlug,
        backgroundImg: bgs.streamingTickets,
      }),
    )
  } else if (hasTickets) {
    items.push(
      buildTicketsCta(baseEmailCapture, {
        t,
        isGuildMember,
        isInTheaters,
        numberAvailable,
        projectSlug,
        backgroundImg: bgs.streamingTickets,
      }),
    )
  } else {
    items.push(
      buildShareCta(baseEmailCapture, {
        t,
        projectName,
        backgroundImg: bgs.streamingTickets,
      }),
    )
  }

  if (pifEnabled) {
    items.push(
      buildPifCta(baseEmailCapture, {
        t,
        projectSlug,
        backgroundImg: bgs.pif,
      }),
    )
  }

  if (shopifyStore && shopifyStore.storeUrl) {
    items.push(
      buildShopCta(baseEmailCapture, {
        t,
        storeUrl: shopifyStore.storeUrl,
        isGuildMember,
        backgroundImg: bgs.gifts,
      }),
    )
  }

  items.push(baseEmailCapture)

  return items
}

function buildBackgrounds(
  catalogTitle: CatalogMovie | CatalogSeries | null | undefined,
  episodesWithStills?: EpisodeWithStills[] | undefined | null,
): {
  streamingTickets?: string
  signUp?: string
  pif?: string
  gifts?: string
} {
  if (!catalogTitle && !episodesWithStills) return {}

  const aspectRatios = ['4:3', '16:9', '2:1', '1:1']

  const episodeStill1 = episodesWithStills?.find((e) => e?.still1?.cloudinaryPath)?.still1?.cloudinaryPath
  const episodeStill2 = episodesWithStills?.find((e) => e?.still2?.cloudinaryPath)?.still2?.cloudinaryPath
  const episodeStill3 = episodesWithStills?.find((e) => e?.still3?.cloudinaryPath)?.still3?.cloudinaryPath
  const episodeStill4 = episodesWithStills?.find((e) => e?.still4?.cloudinaryPath)?.still4?.cloudinaryPath
  const episode2Still1 = episodesWithStills?.[1]?.still1?.cloudinaryPath
  const episodePoster = episodesWithStills?.find((e) => e?.posterLandscapeCloudinaryPath)?.posterLandscapeCloudinaryPath

  return {
    signUp:
      episodeStill1 ??
      episodePoster ??
      (catalogTitle &&
        formatNonTitleLikeLandscapeImagePath(catalogTitle, aspectRatios, [
          'still_1',
          'still_5',
          'non_title_art',
          'backdrop',
        ])),
    streamingTickets:
      episodeStill2 ??
      episodePoster ??
      (catalogTitle &&
        formatNonTitleLikeLandscapeImagePath(catalogTitle, aspectRatios, [
          'still_2',
          'still_5',
          'non_title_art',
          'backdrop',
        ])),
    pif:
      episodeStill3 ??
      episodePoster ??
      (catalogTitle &&
        formatNonTitleLikeLandscapeImagePath(catalogTitle, aspectRatios, [
          'still_3',
          'still_5',
          'non_title_art',
          'backdrop',
        ])),
    gifts:
      episodeStill4 ??
      episode2Still1 ??
      episodePoster ??
      (catalogTitle &&
        formatNonTitleLikeLandscapeImagePath(catalogTitle, aspectRatios, [
          'still_4',
          'still_5',
          'non_title_art',
          'backdrop',
        ])),
  }
}

const withBrightness = (img?: string) => `/e_brightness_hsb:-45/${img}`

function buildBaseEmailCapture(t: TranslateFunction, signUpBackgroundImg?: string): CallToAction {
  return {
    backgroundImageUrl: withBrightness(signUpBackgroundImg),
    buttonHref: null,
    buttonText: null,
    description: t('signUpNotificationsAngel', 'Sign up to get Notifications from Angel'),
    emailListId: ANGEL_STUDIOS_EMAIL_GROUP_ID,
    guildButtonHref: null,
    guildButtonText: null,
    icon: null,
    iconImageUrl: '/v1688092375/angel-app/the-shift/mail-outline',
    marketingFocus: 'email-capture',
    title: t('signUpForUpdates', 'Sign Up For Updates'),
    type: 'Email Capture',
    sys: { id: 'auto-email-capture-default' },
    customCta: true,
  }
}

function buildShopCta(
  base: CallToAction,
  opts: { t: TranslateFunction; storeUrl: string; isGuildMember: boolean; backgroundImg?: string },
): CallToAction {
  const { t, storeUrl, isGuildMember, backgroundImg } = opts
  return {
    ...base,
    backgroundImageUrl: withBrightness(backgroundImg),
    buttonHref: storeUrl,
    buttonText: t('shopNow', 'Shop Now'),
    description: t('shopTheOfficialStore', 'Shop the Official Store'),
    guildButtonHref: isGuildMember ? '/watch' : null,
    guildButtonText: isGuildMember ? t('watchNow', 'Watch Now') : null,
    icon: null,
    iconImageUrl: '/v1705424333/angel-app/roku/photon-icons/unfocused/gift-default',
    title: t('giftsYoullLove', `Gifts You'll Love`),
    type: 'Button',
    sys: { id: 'auto-gifts-youll-love' },
  }
}

function buildStreamingCta(
  base: CallToAction,
  opts: {
    t: TranslateFunction
    inGuildAccess: boolean
    isGuildMember: boolean
    projectSlug: string
    backgroundImg?: string
  },
): CallToAction {
  const { t, inGuildAccess, isGuildMember, projectSlug, backgroundImg } = opts
  return {
    ...base,
    backgroundImageUrl: withBrightness(backgroundImg),
    buttonHref: `${paths.guild.join}/${projectSlug}`,
    buttonText: t('joinToWatchBase', 'Join to Watch'),
    description: inGuildAccess
      ? t('nowStreamingAngelGuildMembers', 'Now Streaming for Angel Guild Members')
      : t('nowStreaming', 'Now Streaming'),
    guildButtonHref: isGuildMember ? `${paths.watch.index}/${projectSlug}` : null,
    guildButtonText: isGuildMember ? t('watchNow', 'Watch Now') : null,
    icon: null,
    iconImageUrl: isGuildMember
      ? '/v1705424407/angel-app/roku/photon-icons/unfocused/watch-default.png'
      : '/v1699039266/angel-studios/icons/icon-guild-outline',
    title:
      inGuildAccess && !isGuildMember
        ? t('joinTheGuildToWatch', 'Join the Guild to Watch')
        : t('nowStreaming', 'Now Streaming'),
    type: 'Button',
    sys: { id: 'auto-watch-streaming' },
  }
}

function buildTicketsCta(
  base: CallToAction,
  opts: {
    t: TranslateFunction
    isGuildMember: boolean
    isInTheaters: boolean
    numberAvailable?: number
    projectSlug: string
    backgroundImg?: string
  },
): CallToAction {
  const { t, isGuildMember, isInTheaters, numberAvailable, projectSlug, backgroundImg } = opts
  const guildHasComp = isGuildMember && (numberAvailable ?? 0) > 0
  return {
    ...base,
    backgroundImageUrl: withBrightness(backgroundImg),
    buttonHref: `${paths.tickets.index}/${projectSlug}`,
    buttonText: t('getShowtimes', 'Get Showtimes'),
    description: guildHasComp
      ? t('claimComplimentaryGuildTicketsToday', 'Claim your two complimentary Guild tickets today')
      : t('bookSeatsToday', 'Book your seats today'),
    guildButtonHref: `${paths.tickets.index}/${projectSlug}`,
    guildButtonText: guildHasComp ? t('claimTickets', 'Claim Tickets') : t('getShowtimes', 'Get Showtimes'),
    icon: null,
    iconImageUrl: '/v1705424392/angel-app/roku/photon-icons/unfocused/ticket-default',
    title: isInTheaters
      ? t('common:inTheatersNow', 'In Theaters Now')
      : t('comingSoonToTheaters', 'Coming Soon to Theaters'),
    type: 'Button',
    sys: { id: 'auto-get-showtimes' },
  }
}

function buildShareCta(
  base: CallToAction,
  opts: {
    t: TranslateFunction
    projectName: string
    backgroundImg?: string
  },
): CallToAction {
  const { t, projectName, backgroundImg } = opts
  return {
    ...base,
    backgroundImageUrl: withBrightness(backgroundImg),
    buttonHref: '#share-section',
    buttonText: t('socialShare', 'Share'),
    description: t('shareExcitingProject', 'Share this exciting project'),
    guildButtonHref: '#share-section',
    guildButtonText: t('socialShare', 'Share'),
    icon: null,
    iconImageUrl: '/v1706201968/angel-app/roku/photon-icons/share-arrow',
    title: `${t('socialShare', 'Share')} ${projectName}`,
    type: 'Button',
    sys: { id: 'auto-get-showtimes' },
  }
}

function buildPifCta(
  base: CallToAction,
  opts: { t: TranslateFunction; projectSlug: string; backgroundImg?: string },
): CallToAction {
  const { t, projectSlug, backgroundImg } = opts
  return {
    ...base,
    backgroundImageUrl: withBrightness(backgroundImg),
    buttonHref: `${paths.payItForward.index}/${projectSlug}`,
    buttonText: t('payItForward', `Pay it Forward`),
    description: t('supportAngelsMissionAmplify', `Support Angel's Mission to Amplify Light`),
    guildButtonHref: null,
    guildButtonText: null,
    icon: null,
    iconImageUrl: '/v1706201929/angel-app/roku/photon-icons/pay-it-forward-outline',
    title: t('payItForward', `Pay it Forward`),
    type: 'Button',
    sys: { id: 'auto-pay-it-forward' },
  }
}

type BaseCallToAction = NonNullable<
  NonNullable<GetTheatricalPreReleasePageDataResult['ctaSquaresCollection']>['items'][number]
>

export type CallToAction = BaseCallToAction & {
  customCta?: boolean
  backgroundImageUrl?: string
  iconImageUrl?: string
}

export interface CtaSquareCollectionProps {
  catalogTitle?: CatalogMovie | CatalogSeries
  numberAvailable?: number
}

export function formatNumColumns(numItems: number): 1 | 2 | 3 {
  if (numItems > 4) return 3
  if (numItems % 3 === 0) return 3
  if (numItems % 2 === 0) return 2
  return 1
}

export function formatColumnClassName(numColumns: 1 | 2 | 3): string {
  return ['grid-cols-1', 'md:grid-cols-2', 'md:grid-cols-3'][numColumns - 1]!
}

export function formatCtaClassName(numColumns: 1 | 2 | 3): string {
  return numColumns === 1 || numColumns % 3 === 0 ? 'md:aspect-h-1 md:aspect-w-1' : 'md:aspect-h-2 md:aspect-w-3'
}

export function filterToApplicableCtas(ctas: CallToAction[], isGuildMember: boolean): CallToAction[] {
  if (!ctas) return []

  return ctas.filter(Boolean).filter(needsGuildJoinCta.bind(null, isGuildMember))
}

function needsGuildJoinCta(isGuildMember: boolean, cta: CallToAction): boolean {
  if (cta.marketingFocus === 'join-the-guild') return !isGuildMember
  return true
}
